import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Content from "../../components/Content/Content";
import { ContentPageClient } from "../../clients/ContentPageClient";
import UpIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import "./ContentPage.css";
import { cleanUpTiles } from "../../utils/contentful-helpers";
import { IconButton } from "@mui/material";
import { useWithSessionData } from "../../hooks/use-with-session-data";
import { handleButtonClickEventBuilder, pageStartEventBuilder, sendEvent } from "../../utils/event-helpers";
import { PreSurveyModal } from "../../components/PreSurveyModal/PreSurveyModal";
import { useWithLtikSession } from "../../hooks/use-with-ltik-session";


export const ContentPage = ({ completedPreSurvey, isLtikSession }: { completedPreSurvey: boolean, isLtikSession: boolean }) => {
  const [contentData, setContentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { userId, sessionId, sessionIdLoaded, userIdLoaded } = useWithSessionData();
  const { canvasId } = useWithLtikSession();
  const navigate = useNavigate();
  
  const sortContentByTags = useCallback((content) => {
    const orderTags = ["1", "2", "3", "4"];
    return content.sort((a, b) => {
      const aIndex = orderTags.findIndex((tag) => a.tags.includes(tag));
      const bIndex = orderTags.findIndex((tag) => b.tags.includes(tag));
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await ContentPageClient.getEntries({
          content_type: "contentPage",
        });
        const cleanedData = cleanUpTiles(response.items);
        const sortedData = sortContentByTags(cleanedData);
        setContentData(sortedData);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching content:", err);
        setError("Failed to fetch content. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [sortContentByTags]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) =>
      prev < contentData.length - 1 ? prev + 1 : prev
    );
  };

  const handleLetsGo = () => {
    const event = handleButtonClickEventBuilder(userId, sessionId, "Let's Go", {});
    sendEvent(event);
    navigate('/mentorpal');
  };

  useEffect(() => {
    if(!sessionIdLoaded || !userIdLoaded) return;
    const pageUrl = window.location.origin + window.location.pathname;
    const curSlide = contentData[currentSlide];
    if(!curSlide) return;
    const startPageEvent = pageStartEventBuilder(userId, sessionId, pageUrl, {
      slide: curSlide.title,
      slideIndex: currentSlide,
      totalSlides: contentData.length,
    });
    sendEvent(startPageEvent);
  }, [currentSlide, sessionIdLoaded, userIdLoaded, userId, sessionId, contentData]);

  if (isLoading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;


  return (
    <div className="content-page">
      <PreSurveyModal open={!completedPreSurvey && isLtikSession} canvasId={canvasId} />
      {contentData.length > 0 ? (
        <div className="carousel">
          {currentSlide > 0 && (
            <div className="arrow-left arrow">
            <IconButton
            onClick={handlePrevSlide}
            aria-label="Previous Slide"
            style={{ padding: "0" }}
          >
            <UpIcon style={{ transform: "rotate(-90deg)", fontSize: "4rem" }} />
          </IconButton>
          </div>
          )}
          <div className="slides-container">
            {contentData.map((content, index) => (
              <div
                key={content.id}
                className={
                  index === currentSlide ? "slide" : "slide hidden-slide"
                }
              >
                <Content
                  title={content.title}
                  displayName={content.displayName}
                  description={content.description}
                  images={content.images}
                />
              </div>
            ))}
          </div>
          {currentSlide < contentData.length - 1 ? (
            <div className="arrow-right arrow">
            <IconButton
              onClick={handleNextSlide}
              aria-label="Next Slide"
              style={{ padding: "0" }}
            >
              <UpIcon style={{ transform: "rotate(90deg)", fontSize: "4rem", }} />
            </IconButton>
            </div>
          ) : (
            <button
              onClick={handleLetsGo}
              className="lets-go-button"
              aria-label="Let's Go"
            >
              Let's go
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={handleLetsGo}
          className="lets-go-button"
          aria-label="Let's Go"
        >
          Let's go
        </button>
      )}
    </div>
  );
};

export default ContentPage;